// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import Map, { Source, Layer } from 'react-map-gl';
import { Box, useTheme } from '@mui/system';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { getOrderIds, getOrders } from 'Features/order/orderSelectors';
import { RootState } from 'Store';
import { getRasterRequestStatus } from 'Features/project/projectSelectors';
import { GetOrdersThunk } from 'Features/order/orderThunk';
import { LoadKmlsThunk } from 'Features/kml/kmlThunk';
import Autosuggest from 'Features/project/Sidebar/Autosuggest';
import MapControls from './MapControls';
import MapRasterLayer from './MapRasterLayer';
import MapVectorLayers from './MapVectorLayers';
import MapLayersController from './MapLayersController';
import MapMeasurements from './MapMeasurements';
import MapDraw from './MapDraw';
import MapLasBBox from './MapLasBBox';
import ErrorPaper from './FileErrors';
import MapEditor from './MapEditor';
import { SetCanvasSizeAction, SetDxfExtentAction, SetLatAction, SetLayerVisibleAction, SetLngAction, SetScreenshotAction, SetViewportAction } from './mapCommonActions';
import { InitMapThunk, ToggleNoneVectorTiles } from './mapCommonThunk';
import MapKMLLayer from './MapKMLLayer';
import MapSearchMarker from './MapSearchMarker';
import MapStyleToggle from './MapStyleToggle';
import MapResizeObserver from './MapResizeObserver';
import stylesDef from './styles';
import { ResetDrawSource, ResetUpdateEntities } from './mapEditorThunk';
import { SetCurrentLayerAction, SetlayerSelectedAction } from './mapEditorActions';

// By adding this layer to the map on the admin portal, we can make sure to position the raster layer at the bottom by using the beforeId property on the raster layer.
// That way, the Kml layer and the DXF layers always appear on top of the tif
const EmptyLayer = () => (
  <Source id="kmlSource" type="geojson" data={{ type: 'FeatureCollection', features: [] }}>
    <Layer id="kmlStub" source="kmlSource" type="fill" />
  </Source>
);

interface ComponentProps {
  mode: string;
}

const getMapboxBasemapStyle = (mapStyle: string) => {
  // this is to get the mapbox basemap style
  if (mapStyle === 'streets') {
    return 'mapbox://styles/mapbox/streets-v11';
  }
  return 'mapbox://styles/mapbox/satellite-v9';
};

const getEsriBasemapStyle = (mapStyle: string) => {
  // this is to get the esri basemap style
  if (mapStyle === 'streets') {
    return {
      version: 8,
      // this is to set the mapbox fonts for the esri basemap
      // so that the text will be visible on the esri basemap
      glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
      sources: {
        'arcgis-basemap': {
          type: 'raster',
          tiles: [`https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}?token=${_ESRI_API_Key_}`],
          tileSize: 256,
          minzoom: 0,
          maxzoom: 19,
        },
      },
      layers: [
        {
          id: 'arcgis-basemap-layer',
          type: 'raster',
          source: 'arcgis-basemap',
        },
      ],
    };
  }
  return {
    version: 8,
    glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
    sources: {
      'arcgis-basemap': {
        type: 'raster',
        tiles: [`https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=${_ESRI_API_Key_}`],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 19,
      },
    },
    layers: [
      {
        id: 'arcgis-basemap-layer',
        type: 'raster',
        source: 'arcgis-basemap',
      },
    ],
  };
};

const CreateMap: React.FC<ComponentProps> = React.memo(({ mode }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const interactive = mode !== 'sidebar';
  const showLayers = mode === 'layers';
  const rasterTab = mode === 'details';
  const [basemapStyle, setBasemapStyle] = useState<string | object>('mapbox://styles/mapbox/streets-v11');
  const viewportCenter = useAppSelector((state) => state.map.common.viewport?.center);
  const viewportZoom = useAppSelector((state) => state.map.common.viewport?.zoom);
  const rasterTileJsonLoading = useAppSelector((state) => state.map.common.rasterTileJsonLoading);
  const lasBBoxLoading = useAppSelector((state) => state.map.common.lasBBoxLoading);
  const baseMapLayer = useAppSelector((state) => state.map.common.layers?.baseMap);
  const mapStyle = useAppSelector((state) => state.map.common.mapStyle);
  const editor = useAppSelector((state) => state.map.editor.present.editorMode);
  const fileVersions = useAppSelector((state) => state.order.fileVersions);
  const vectorTileJson = useAppSelector((state) => state.map.common.vectorTileJson);
  const basemap = useAppSelector((state) => state.map.common.layers?.baseMap);
  const layerVisible = useAppSelector((state) => state.map.common.layerVisible);

  const orderIds = useSelector((state: RootState) => getOrderIds(state));
  const projectId = useAppSelector((state) => state.project.projectId);
  const imageryRequest = useAppSelector((state) => state.project.project?.imageryRequest);
  const resources = useAppSelector((state) => state.auth.resources);
  const opsTrainer = 'opsTrainer' in resources;
  const editorFunctions = 'editorFunctions' in resources;
  const { esriBasemap } = useFlags();
  const isEstimate = useSelector((state: RootState) => getOrders(state).activeOrder?.isEstimate);

  const imageryRequested = (isEstimate && !!(imageryRequest?.requested)) || !!(imageryRequest?.uploaded) || !!(imageryRequest?.notificationSent);
  const rasterizationRequested = useSelector((state: RootState) => getRasterRequestStatus(state)).requested;

  useEffect(() => {
    dispatch(SetLayerVisibleAction(null));
  }, []);

  // Drop any stale DXF extent on project change and on unmount so it can't refit a later map.
  useEffect(() => {
    dispatch(SetDxfExtentAction(null));
    return () => {
      dispatch(SetDxfExtentAction(null));
    };
  }, [projectId]);

  const getLatLong = useCallback((e: any) => {
    dispatch(SetLatAction(JSON.stringify(e.lngLat.lat)));
    dispatch(SetLngAction(JSON.stringify(e.lngLat.lng)));
  }, []);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current
      || !ref.current.getBoundingClientRect().width
      || !ref.current.getBoundingClientRect().height) return;

    const { width, height } = ref.current.getBoundingClientRect();
    dispatch(SetCanvasSizeAction([width, height]));
  }, [ref.current]);

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const effect = async () => {
      setInitialized(false);
      if (opsTrainer && showLayers) {
        await dispatch(GetOrdersThunk(projectId));
        await dispatch(LoadKmlsThunk());
      }
      await dispatch(InitMapThunk(showLayers));
      if (_ADMIN_ && showLayers) {
        dispatch(ToggleNoneVectorTiles());
      }
      setInitialized(true);
    };
    effect();
  }, [rasterTileJsonLoading, lasBBoxLoading, projectId]);

  useEffect(() => {
    if (!layerVisible && orderIds.length && fileVersions && vectorTileJson) {
      const visible: any = {};
      orderIds.forEach((orderId) => {
        const fileVersion = fileVersions[orderId];
        const tileJson = fileVersion && vectorTileJson[orderId] && vectorTileJson[orderId][fileVersion];
        tileJson?.vector_layers?.forEach((layer: any) => {
          const id = `default-${orderId}-${layer.id}`;
          visible[id] = true;
        });
      });
      dispatch(SetLayerVisibleAction(visible));
    }
  }, [orderIds, fileVersions, vectorTileJson, layerVisible]);

  useEffect(() => {
    if (!basemap) {
      // set empty basemap
      setBasemapStyle({ version: 8, sources: {}, layers: [] });
    } else {
      const showMapboxLayers = !showLayers || baseMapLayer;
      if (!showMapboxLayers) {
        setBasemapStyle('mapbox://styles/mapbox/empty-v8');
      } else if (esriBasemap) {
        setBasemapStyle(getEsriBasemapStyle(mapStyle));
      } else {
        setBasemapStyle(getMapboxBasemapStyle(mapStyle));
      }
    }
  }, [mapStyle, basemap]);

  const onMove = useCallback((evt) => {
    const { latitude, longitude, zoom } = evt.viewState;
    dispatch(SetViewportAction({ center: [longitude, latitude], zoom }));
  }, []);

  const onIdle = useCallback((evt) => {
    if (mode === 'sidebar' || ((imageryRequested || rasterizationRequested) && mode === 'details')) {
      const img = evt.target.getCanvas().toDataURL();
      dispatch(SetScreenshotAction(img));
    }
  }, []);

  useEffect(() => {
    if (!editor) {
      if (layerVisible) {
        const visible: any = {};
        Object.keys(layerVisible).forEach((layer: any) => {
          visible[layer] = true;
        });
        dispatch(SetLayerVisibleAction(visible));
      }
      dispatch(SetCurrentLayerAction());
      dispatch(SetlayerSelectedAction());
      dispatch(ResetDrawSource());
      dispatch(ResetUpdateEntities());
    }
  }, [editor]);

  return (
    <>
      <Box sx={styles.map} ref={ref}>
        {mode === 'details' && (
          <Box sx={showLayers ? styles.mapSearchOverlayLayers : styles.mapSearchOverlay}>
            <Autosuggest />
          </Box>
        )}
        {initialized && (
          <Map
            // reuseMaps // this prop will cause empty map when switch between Project Details and CAD View
            mapboxAccessToken={_MAPBOX_KEY_}
            mapStyle={basemapStyle}
            longitude={viewportCenter[0]}
            latitude={viewportCenter[1]}
            zoom={viewportZoom}
            onMove={onMove}
            onMouseMove={getLatLong}
            onIdle={onIdle}
            onError={() => {}}
          >
            <>
              <MapResizeObserver />
              <EmptyLayer />
              {rasterTab && <ErrorPaper />}
              <MapSearchMarker />
              <MapRasterLayer showLayers={showLayers} />
              <MapLayersController showLayers={showLayers} />
              <MapVectorLayers showLayers={showLayers} />
              {(!showLayers || _ADMIN_) && <MapLasBBox showLabel={mode !== 'sidebar'} />}
              {(showLayers && !editor && !_ADMIN_) && <MapMeasurements />}
              {(showLayers && editor && editorFunctions && !_ADMIN_) && <MapEditor />}
              <MapKMLLayer />
              {!showLayers && <MapDraw />}
            </>
          </Map>
        )}
        {!interactive && <Box sx={styles.transparentLayer} />}
      </Box>
      {!initialized && _ADMIN_ && showLayers && <Box sx={styles.loadingModal}><p>Loading...</p></Box>}

      {/* Checking for rasterLoading in the CAD View page */}
      {(!rasterTab && rasterTileJsonLoading && interactive) && editorFunctions && <Box sx={styles.loadingModal}><p>Loading...</p></Box>}

      {/* Checking for rasterLoading in the project details page */}
      {(rasterTab && (rasterTileJsonLoading) && interactive) && <Box sx={styles.loadingModal}><p>Loading...</p></Box>}

      {(interactive && !opsTrainer && !_ADMIN_) && <MapControls showLayers={showLayers} />}
      {interactive && <MapStyleToggle />}
    </>
  );
});

CreateMap.propTypes = {
  mode: PropTypes.string.isRequired,
};

export default CreateMap;
